import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HistoryIcon } from 'lucide-react';

import { ActionButton } from '@/shared/ui/action-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldGroup } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';

import { ConsignmentHistoryModal } from '@/widgets/consignment-history-modal';
import { ConsignmentStatusSelect } from '@/features/consignment-actions';
import type { ConsignmentSummary } from '@/entities/consignments';
import { FilterSelect, FormInput, FormField } from '@/shared/ui';

import { ConsignmentFormValues, getPriorityOptions } from '../model';

interface ConsignmentControlCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
  isReadOnly?: boolean;
  consignmentId?: string;
  consignment?: ConsignmentSummary;
}

export function ConsignmentControlCard({ form, isReadOnly, consignmentId, consignment }: ConsignmentControlCardProps) {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors }
  } = form;

  const priorityOptions = useMemo(() => getPriorityOptions(t), [t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.control.box')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FormField label={t('request.status.label')} name="status">
            <ConsignmentStatusSelect consignmentId={consignmentId} consignment={consignment} />
          </FormField>

          <FormField label={t('consignment.fields.priority.label')} name="priority" error={errors.priority?.message}>
            <Controller
              name="priority"
              control={control}
              defaultValue="normal"
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['priority']>
                  value={field.value}
                  onChange={field.onChange}
                  options={priorityOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <div className="flex justify-end">
            {consignmentId && (
              <ConsignmentHistoryModal
                consignmentId={consignmentId}
                trigger={
                  <ActionButton type="button" intent="view" size="sm">
                    <HistoryIcon className="mr-2 h-4 w-4" />
                    {t('newcmr.showhistory.button')}
                  </ActionButton>
                }
              />
            )}
          </div>

          <FormField label={t('requestHistory.remarks.desc')} name="remarks" error={errors.remarks?.message}>
            <Textarea
              id="remarks"
              {...register('remarks')}
              placeholder={t('requestHistory.remarks.desc')}
              rows={4}
              disabled={isReadOnly}
            />
          </FormField>

          <FormInput
            label={t('tracking.servicereq.overpackingqty.label')}
            id="transportUnitQuantity"
            type="number"
            registration={register('transportUnitQuantity', { valueAsNumber: true })}
            placeholder={t('consignment.fields.transportUnitQuantity.placeholder')}
            disabled={isReadOnly}
            error={errors.transportUnitQuantity?.message}
          />
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
